import { Card, Group, Stack, Text, Button, Avatar, Flex, Box, Badge, Anchor, Tooltip } from "@mantine/core";
import {
  IconBuilding,
  IconMapPin,
  IconRosetteDiscountCheckFilled,
  IconEye,
  IconX,
  IconCheck,
  IconClock,
  IconFileText,
} from "@tabler/icons-react";
import { getIndustryLabel, CollaborationStatus } from "@/components/shared/constants/general.const";
import { useDisclosure } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import AcceptInterestModal from "@/pages/interests/components/AcceptInterestModal";
import CollaborationStepper from "@/pages/interests/components/CollaborationStepper";

// ─── TYPES ────────────────────────────────────────────────────────────────────
type CompanyInfo = {
  id: string;
  gtid: string;
  legalName?: string;
  headquartersCountry?: string;
  industryCategory?: string[];
  coreCapabilities?: string[];
  status?: string;
};

interface InterestCardProps {
  matchId: string;
  company: CompanyInfo;
  collaborationStatus: string;
  isRequester: boolean;
  requesterPaid: boolean;
  receiverPaid: boolean;
  requesterSignedNDA: boolean;
  receiverSignedNDA: boolean;
  paymentDeadline?: Date | string | null;
  receivedAt?: Date | string;
  onAccept?: (matchId: string) => void;
  onDecline?: (matchId: string) => void;
  onMakePayment?: (matchId: string) => void;
  onViewProfile?: (companyId: string) => void;
  onOpenWorkspace?: (matchId: string) => void;
  onRedeemPayment?: (matchId: string) => void;
  isLoading?: boolean;
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function getDaysRemaining(paymentDeadline?: Date | string | null): number | null {
  if (!paymentDeadline) return null;
  const diff = new Date(paymentDeadline).getTime() - Date.now();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function InterestCard({
  matchId,
  company,
  collaborationStatus,
  isRequester,
  requesterPaid,
  receiverPaid,
  requesterSignedNDA,
  receiverSignedNDA,
  paymentDeadline,
  onAccept,
  onDecline,
  onMakePayment,
  onViewProfile,
  onOpenWorkspace,
  isLoading = false,
}: InterestCardProps) {
  const [acceptModalOpened, { open: openAcceptModal, close: closeAcceptModal }] = useDisclosure(false);
  const navigate = useNavigate();

  const userPaid = isRequester ? requesterPaid : receiverPaid;

  if (!company) return null;

  const daysRemaining = getDaysRemaining(paymentDeadline);
  const isActive = collaborationStatus === CollaborationStatus.BOTH_PAID;

  // NDA status checks using props instead of fetching data
  const currentUserSignedNDA = isRequester ? requesterSignedNDA : receiverSignedNDA;
  const otherUserSignedNDA = isRequester ? receiverSignedNDA : requesterSignedNDA;
  const bothSignedNda = requesterSignedNDA && receiverSignedNDA;

  // ── single CTA ──────────────────────────────────────────────────────────────
  const renderCta = () => {
    // 1. Pending request (receiver side) → Accept / Decline
    if (collaborationStatus === CollaborationStatus.REQUESTED && !isRequester) {
      return (
        <>
          <Button
            variant="filled"
            color="primary"
            size="sm"
            radius="lg"
            leftSection={<IconCheck size={16} />}
            onClick={openAcceptModal}
            loading={isLoading}
          >
            Accept
          </Button>
          <Button
            variant="outline"
            color="gray"
            size="sm"
            radius="lg"
            leftSection={<IconX size={16} />}
            onClick={() => onDecline?.(matchId)}
            loading={isLoading}
          >
            Decline
          </Button>
        </>
      );
    }

    // 2. Request declined by them (requester side) → Show "Request Declined" text
    if (collaborationStatus === CollaborationStatus.RECEIVER_DECLINED && isRequester) {
      return (
        <Text size="sm" c="red.6" fw={500}>
          Request Declined
        </Text>
      );
    }

    // 3. Accepted but current user hasn't paid → Make Payment
    if (
      (collaborationStatus === CollaborationStatus.BOTH_ACCEPTED ||
        collaborationStatus === CollaborationStatus.REQUESTER_PAID_ONLY ||
        collaborationStatus === CollaborationStatus.RECEIVER_PAID_ONLY) &&
      !userPaid
    ) {
      return (
        <Button
          variant="filled"
          color="primary"
          size="sm"
          radius="lg"
          onClick={() => onMakePayment?.(matchId)}
          loading={isLoading}
        >
          Make Payment
        </Button>
      );
    }

    // 4. Current user paid (regardless of other party's payment) → Show NDA / Workspace buttons
    if (userPaid) {
      // No NDA or current user hasn't signed → Sign NDA button
      if (!currentUserSignedNDA) {
        return (
          <Button
            variant="filled"
            color="primary"
            size="sm"
            radius="lg"
            leftSection={<IconFileText size={16} />}
            onClick={() => navigate(`/requests/${company.id}/nda`)}
            loading={isLoading}
          >
            Sign NDA
          </Button>
        );
      }

      // Current user signed, waiting for other party to sign NDA → Disabled Open Workspace with tooltip
      if (currentUserSignedNDA && !otherUserSignedNDA) {
        return (
          <Tooltip label="Waiting for them to sign NDA" withArrow>
            <Button variant="filled" color="green" size="sm" radius="lg" disabled>
              Open Workspace
            </Button>
          </Tooltip>
        );
      }

      // Both signed NDA but other party hasn't paid yet → Disabled Open Workspace with tooltip
      if (bothSignedNda && collaborationStatus !== CollaborationStatus.BOTH_PAID) {
        return (
          <Tooltip label="Waiting for them to make payment" withArrow>
            <Button variant="filled" color="green" size="sm" radius="lg" disabled>
              Open Workspace
            </Button>
          </Tooltip>
        );
      }

      // Both signed NDA and both paid → Open Workspace
      if (bothSignedNda && collaborationStatus === CollaborationStatus.BOTH_PAID) {
        return (
          <Button
            variant="filled"
            color="green"
            size="sm"
            radius="lg"
            onClick={() => onOpenWorkspace?.(matchId)}
            loading={isLoading}
          >
            Open Workspace
          </Button>
        );
      }
    }

    // 5. Expired - no redeem button (auto-redeemed)
    if (collaborationStatus === CollaborationStatus.PAYMENT_EXPIRED) {
      return (
        <Text size="sm" c="orange.6" fw={500}>
          Payment Expired
        </Text>
      );
    }

    return null;
  };

  // ── deadline warning (only while payment window is open) ─────────────────
  const showDeadline =
    paymentDeadline &&
    (collaborationStatus === CollaborationStatus.BOTH_ACCEPTED ||
      collaborationStatus === CollaborationStatus.REQUESTER_PAID_ONLY ||
      collaborationStatus === CollaborationStatus.RECEIVER_PAID_ONLY);

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Card
        p="lg"
        radius="lg"
        withBorder
        style={{
          borderColor: isActive ? "var(--mantine-color-green-3)" : undefined,
          backgroundColor: isActive ? "var(--mantine-color-green-0)" : undefined,
        }}
      >
        <Flex gap={{ base: "sm", sm: "md" }} direction={{ base: "column", sm: "row" }} align="stretch">
          {/* Avatar – desktop only */}
          <Avatar size="lg" radius="lg" variant="light" color={isActive ? "green" : "primary"} visibleFrom="sm">
            <IconBuilding size={24} />
          </Avatar>

          {/* Left Side - Company Info */}
          <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
            {/* Company name row */}
            <Group gap="sm" align="center">
              <Anchor
                onClick={() => onViewProfile?.(company.id)}
                td="none"
                c="inherit"
                styles={{
                  root: {
                    "&:hover": {
                      textDecoration: "underline",
                      cursor: "pointer",
                    },
                  },
                }}
              >
                <Text fw={400} fz={{ base: "md", sm: "lg" }}>
                  {company.gtid || "N/A"}
                </Text>
              </Anchor>
              {company.status === "VERIFIED" && (
                <IconRosetteDiscountCheckFilled size={18} style={{ color: "var(--mantine-color-primary-6)" }} />
              )}
            </Group>

            {/* Industry + location */}
            <Group gap="md" wrap="wrap">
              <Group gap={4}>
                <IconBuilding size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                {Array.isArray(company.industryCategory) && company.industryCategory.length > 0 ? (
                  <Group gap={4}>
                    <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                      {getIndustryLabel(company.industryCategory[0])}
                    </Text>
                    {company.industryCategory.length > 1 && (
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        +{company.industryCategory.length - 1}
                      </Text>
                    )}
                  </Group>
                ) : (
                  <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                    N/A
                  </Text>
                )}
              </Group>
              {company.headquartersCountry && (
                <Group gap={4}>
                  <IconMapPin size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                  <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                    {company.headquartersCountry}
                  </Text>
                </Group>
              )}
            </Group>

            {/* Core capabilities */}
            {company.coreCapabilities && company.coreCapabilities.length > 0 && (
              <Group gap="xs">
                {company.coreCapabilities.slice(0, 3).map((cap, i) => (
                  <Badge
                    key={i}
                    variant="light"
                    color="slate"
                    size="lg"
                    radius="md"
                    fw={400}
                    c="black"
                    styles={{ root: { textTransform: "none" } }}
                  >
                    {cap}
                  </Badge>
                ))}
                {company.coreCapabilities.length > 3 && (
                  <Badge
                    variant="light"
                    color="slate.6"
                    size="lg"
                    radius="md"
                    fw={400}
                    c="slate.6"
                    styles={{ root: { textTransform: "none" } }}
                  >
                    +{company.coreCapabilities.length - 3} more
                  </Badge>
                )}
              </Group>
            )}

            {/* Deadline warning */}
            {showDeadline && (
              <Group gap={4}>
                <IconClock
                  size={14}
                  color={
                    daysRemaining !== null && daysRemaining <= 3
                      ? "var(--mantine-color-orange-6)"
                      : "var(--mantine-color-primary-6)"
                  }
                />
                <Text fz="sm" c={daysRemaining !== null && daysRemaining <= 3 ? "orange.6" : "blue.6"}>
                  {daysRemaining !== null && daysRemaining > 0
                    ? `Payment window expires in ${daysRemaining} ${daysRemaining === 1 ? "day" : "days"}`
                    : "Payment window has expired"}
                </Text>
              </Group>
            )}
          </Stack>

          {/* Right Side - Buttons + Timeline (Desktop) */}
          <Stack
            gap="xl"
            align="flex-end"
            justify="flex-start"
            style={{
              width: 320,
              minWidth: 320,
              flexShrink: 0,
            }}
            visibleFrom="sm"
          >
            {/* CTA Buttons at top */}
            <Group gap="sm" wrap="wrap" justify="flex-end">
              {renderCta()}
              <Button
                variant="outline"
                size="sm"
                radius="lg"
                leftSection={<IconEye size={16} />}
                onClick={() => onViewProfile?.(company.id)}
              >
                View Profile
              </Button>
            </Group>

            {/* Stepper/Timeline */}
            <Box w="100%">
              <CollaborationStepper
                status={collaborationStatus}
                isRequester={isRequester}
                requesterPaid={requesterPaid}
                receiverPaid={receiverPaid}
              />
            </Box>
          </Stack>

          {/* Mobile: Buttons + Timeline stacked below */}
          <Stack gap="xl" hiddenFrom="sm" w="100%">
            {/* CTA Buttons */}
            <Group gap="sm" justify="flex-start" wrap="wrap">
              {renderCta()}
              <Button
                variant="outline"
                size="sm"
                radius="lg"
                leftSection={<IconEye size={16} />}
                onClick={() => onViewProfile?.(company.id)}
              >
                View Profile
              </Button>
            </Group>

            {/* Stepper/Timeline */}
            <Box>
              <CollaborationStepper
                status={collaborationStatus}
                isRequester={isRequester}
                requesterPaid={requesterPaid}
                receiverPaid={receiverPaid}
              />
            </Box>
          </Stack>
        </Flex>
      </Card>

      {/* Accept confirmation modal (unchanged) */}
      <AcceptInterestModal
        opened={acceptModalOpened}
        onClose={closeAcceptModal}
        onConfirm={() => {
          closeAcceptModal();
          onAccept?.(matchId);
        }}
        isLoading={isLoading}
      />
    </>
  );
}
